/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/unbound-method */
import React from 'react';
import { debounce } from 'lodash';
import { BTTService } from 'types/campaign';
import { bttServicesSearch } from 'utils/api/campaign';
import Spinner from 'components/common/next/spinner';
import { ALL_SERVICE_TITLES, B2B_SERVICE_TITLES, B2C_SERVICE_TITLES } from 'constants/common';

import './bttServicesSelect.scss';
import { BusinessType } from 'enums/common';

type TargetGroupType = BusinessType | 'b2b' | 'b2c';

interface Props {
  noResultsText?: string;
  onSelect(service: BTTService): void;
  closeSearchOnSelect?: boolean;
  placeholder?: string;
  targetGroupType?: TargetGroupType;
}

interface State {
  search: string;
  searchResults: BTTService[];
  resultsOpen: boolean;
  loading: boolean;
}

class BttServicesSelect extends React.Component<Props, State> {
  public containerNode: HTMLDivElement | null = null;
  public searchField: HTMLInputElement | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      search: '',
      searchResults: [],
      resultsOpen: false,
      loading: false,
    };
    this.searchService = debounce(this.searchService.bind(this), 300);
    this.changeSearch = this.changeSearch.bind(this);
    this.openSearch = this.openSearch.bind(this);
    this.closeSearch = this.closeSearch.bind(this);
  }

  componentDidMount() {
    void this.loadServices();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.targetGroupType !== this.props.targetGroupType) {
      void this.loadServices();
    }
  }

  getHardcodedServices(): BTTService[] {
    return Object.entries(B2B_SERVICE_TITLES).map(([type, serviceTitle]) => {
      return {
        type,
        title: serviceTitle.title,
        info: serviceTitle.description || '',
        links: [],
        linkUrls: [],
        linkTexts: [],
      };
    });
  }

  async getAvailableServices(): Promise<BTTService[]> {
    if (this.props.targetGroupType === BusinessType.B2b) {
      return this.getHardcodedServices();
    }

    return bttServicesSearch();
  }

  async loadServices() {
    this.setState({ loading: true });
    try {
      const allServices = await this.getAvailableServices();
      const mappedServices = this.mapServices(allServices || []);
      this.setState({ searchResults: mappedServices, loading: false });
    } catch {
      this.setState({ searchResults: [], loading: false });
    }
  }

  async searchService(query: string) {
    if (!query || query.length < 1) {
      void this.loadServices();
      return;
    }
    this.setState({ loading: true });
    try {
      const allServices = await this.getAvailableServices();
      const normalizedQuery = query.toLowerCase();
      const mappedServices = this.mapServices(allServices || []);
      const filteredServices = mappedServices.filter((service) => {
        const type = (service.type || '').toLowerCase();
        const title = (service.title || '').toLowerCase();
        const info = (service.info || '').toLowerCase();
        return type.includes(normalizedQuery) || title.includes(normalizedQuery) || info.includes(normalizedQuery);
      });
      this.setState({ searchResults: filteredServices, loading: false });
    } catch {
      this.setState({ searchResults: [], loading: false });
    }
  }

  getServiceTitles() {
    if (this.props.targetGroupType === BusinessType.B2b) {
      return B2B_SERVICE_TITLES;
    }

    if (this.props.targetGroupType === BusinessType.B2c) {
      return B2C_SERVICE_TITLES;
    }

    return ALL_SERVICE_TITLES;
  }

  mapServices(services: BTTService[]): BTTService[] {
    const serviceTitles = this.getServiceTitles();

    return services
      .filter((service) => !this.props.targetGroupType || Boolean(serviceTitles[service.type]))
      .map((service) => {
        const serviceTitle = serviceTitles[service.type];
        const url = serviceTitle?.url;

        return {
          ...service,
          title: serviceTitle?.title || service.type,
          info: serviceTitle?.description || service.info || '',
          links: url ? [{ url }, ...(service.links || [])] : service.links || [],
          linkUrls: url ? [url, ...(service.linkUrls || [])] : service.linkUrls || [],
        };
      });
  }

  changeSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.currentTarget.value;
    this.setState({ search: value, resultsOpen: true });
    this.searchService(value);
  }

  openSearch() {
    this.setState({ resultsOpen: true });
  }

  closeSearch() {
    this.setState({ resultsOpen: false });
  }

  select(service: BTTService, closeSearch?: boolean) {
    const { onSelect, closeSearchOnSelect } = this.props;
    onSelect(service);
    if (closeSearchOnSelect || closeSearch) {
      this.closeSearch();
      this.setState({ search: service.type || '' });
    }
  }

  render() {
    const { noResultsText } = this.props;
    const { search, searchResults, loading } = this.state;

    return (
      <div className="service-select" ref={(e) => (this.containerNode = e)}>
        <div className="service-search-field form-control">
          <label>Valitse palvelut</label>
          <select
            value={search}
            onChange={(e) => {
              const selectedType = e.target.value;
              const selectedService = searchResults.find((service) => service.type === selectedType);
              if (selectedService) {
                this.select(selectedService, true);
              }
              this.setState({ search: selectedType });
            }}
            disabled={loading}
          >
            <option value="">-- Valitse palvelut --</option>
            {searchResults.map((service, idx) => (
              <option key={`service-opt-${idx}`} value={service.type || ''}>
                {service.title || ''}
              </option>
            ))}
          </select>
          {loading && (
            <div className="loading-results">
              <Spinner />
            </div>
          )}
          {!loading && searchResults.length === 0 && (
            <div className="no-results">{noResultsText || 'Ei hakutuloksia.'}</div>
          )}
        </div>
      </div>
    );
  }
}

export default BttServicesSelect;
